import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function SettingsManager() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["admin-settings"],
    queryFn: () => api.get("/admin/settings").then((r) => r.data),
  });

  const [form, setForm] = useState({
    site_title: "",
    tagline: "",
    contact_email: "",
    about_text: "",
    comments_enabled: true,
    comments_require_approval: false,
    maintenance_mode: false,
    maintenance_message: "",
    maintenance_until: "",
    social_facebook: "",
    social_instagram: "",
    social_youtube: "",
    social_twitter: "",
    social_tiktok: "",
    social_telegram: "",
    newsletter_from_email: "",
    comment_notify_email: "",
    terms_content: "",
    privacy_content: "",
    disclaimer_text: "",
    listing_valid_days: 30,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.checked }));

  const isoToLocalInput = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  };
  const localInputToIso = (val) => {
    if (!val) return "";
    const d = new Date(val);
    return isNaN(d.getTime()) ? "" : d.toISOString();
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/settings", form);
      qc.invalidateQueries({ queryKey: ["settings"] });
      qc.invalidateQueries({ queryKey: ["admin-settings"] });
      toast.success("Настройките са запазени.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20";
  const labelClass = "mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500";

  return (
    <div className="mt-6 max-w-2xl" data-testid="settings-manager">
      <h2 className="mb-4 font-display text-lg font-bold text-slate-900">Настройки на сайта</h2>
      <form onSubmit={save} className="space-y-5 rounded-xl border border-slate-200 bg-white p-6">
        <div>
          <label className={labelClass}>Име на сайта</label>
          <input className={inputClass} data-testid="settings-title" value={form.site_title} onChange={set("site_title")} />
        </div>
        <div>
          <label className={labelClass}>Слоган</label>
          <input className={inputClass} data-testid="settings-tagline" value={form.tagline} onChange={set("tagline")} />
        </div>
        <div>
          <label className={labelClass}>Имейл за контакт</label>
          <input type="email" className={inputClass} data-testid="settings-email" value={form.contact_email} onChange={set("contact_email")} />
        </div>
        <div>
          <label className={labelClass}>Текст „За нас“</label>
          <textarea rows={5} className={inputClass} data-testid="settings-about" value={form.about_text} onChange={set("about_text")} />
        </div>

        <div className="space-y-3 rounded-lg bg-slate-50 p-4">
          <div className="text-xs font-bold uppercase tracking-wide text-slate-500">Социални мрежи</div>
          <p className="text-xs text-slate-500">Поставете пълния линк към профила. Празните полета не се показват на сайта.</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className={labelClass}>Facebook</label>
              <input className={inputClass} data-testid="settings-social-facebook" value={form.social_facebook || ""} onChange={set("social_facebook")} placeholder="https://facebook.com/..." />
            </div>
            <div>
              <label className={labelClass}>Instagram</label>
              <input className={inputClass} data-testid="settings-social-instagram" value={form.social_instagram || ""} onChange={set("social_instagram")} placeholder="https://instagram.com/..." />
            </div>
            <div>
              <label className={labelClass}>YouTube</label>
              <input className={inputClass} data-testid="settings-social-youtube" value={form.social_youtube || ""} onChange={set("social_youtube")} placeholder="https://youtube.com/@..." />
            </div>
            <div>
              <label className={labelClass}>X (Twitter)</label>
              <input className={inputClass} data-testid="settings-social-twitter" value={form.social_twitter || ""} onChange={set("social_twitter")} placeholder="https://x.com/..." />
            </div>
            <div>
              <label className={labelClass}>TikTok</label>
              <input className={inputClass} data-testid="settings-social-tiktok" value={form.social_tiktok || ""} onChange={set("social_tiktok")} placeholder="https://tiktok.com/@..." />
            </div>
            <div>
              <label className={labelClass}>Telegram</label>
              <input className={inputClass} data-testid="settings-social-telegram" value={form.social_telegram || ""} onChange={set("social_telegram")} placeholder="https://t.me/..." />
            </div>
          </div>
        </div>

        <div className={`space-y-3 rounded-lg border p-4 ${form.maintenance_mode ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-slate-50"}`}>
          <div className="flex items-center justify-between">
            <div className="text-xs font-bold uppercase tracking-wide text-slate-500">Режим „в процес на обновяване“</div>
            {form.maintenance_mode && (
              <span className="rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white" data-testid="maintenance-active-badge">Активен</span>
            )}
          </div>
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            <input type="checkbox" data-testid="settings-maintenance-mode" checked={!!form.maintenance_mode} onChange={toggle("maintenance_mode")} className="h-4 w-4 accent-[#0055D4]" />
            Пусни режим на поддръжка (сайтът е скрит за посетителите)
          </label>
          <p className="text-xs text-slate-500">
            Когато е включен, посетителите виждат страница „Страницата е в процес на обновяване“. Вие
            като администратор продължавате да виждате сайта нормално.
          </p>
          <div>
            <label className={labelClass}>Съобщение към посетителите</label>
            <textarea rows={3} className={inputClass} data-testid="settings-maintenance-message" value={form.maintenance_message || ""} onChange={set("maintenance_message")} placeholder="Работим по подобрения и ще се върнем съвсем скоро." />
          </div>
          <div>
            <label className={labelClass}>Часът, в който се връщаме (по избор)</label>
            <div className="flex items-center gap-2">
              <input
                type="datetime-local"
                className={inputClass}
                data-testid="settings-maintenance-until"
                value={isoToLocalInput(form.maintenance_until)}
                onChange={(e) => setForm((f) => ({ ...f, maintenance_until: localInputToIso(e.target.value) }))}
              />
              {form.maintenance_until && (
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, maintenance_until: "" }))}
                  data-testid="settings-maintenance-until-clear"
                  className="shrink-0 rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-500 hover:bg-slate-50"
                >
                  Изчисти
                </button>
              )}
            </div>
            <p className="mt-1 text-xs text-slate-500">Ако е зададен, посетителите виждат обратно броене „Връщаме се след…“.</p>
          </div>
        </div>

        <div className="space-y-3 rounded-lg bg-slate-50 p-4">
          <div className="text-xs font-bold uppercase tracking-wide text-slate-500">Коментари</div>
          <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
            <input type="checkbox" data-testid="settings-comments-enabled" checked={!!form.comments_enabled} onChange={toggle("comments_enabled")} className="h-4 w-4 accent-[#0055D4]" />
            Разреши коментари под статиите
          </label>
          <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
            <input type="checkbox" data-testid="settings-comments-approval" checked={!!form.comments_require_approval} onChange={toggle("comments_require_approval")} className="h-4 w-4 accent-[#0055D4]" />
            Коментарите изискват одобрение преди публикуване
          </label>
        </div>

        <div className="space-y-4 rounded-lg bg-slate-50 p-4">
          <div className="text-xs font-bold uppercase tracking-wide text-slate-500">Имейл / Бюлетин</div>
          <div>
            <label className={labelClass}>Имейл на подателя (верифициран в SendGrid)</label>
            <input type="email" className={inputClass} data-testid="settings-from-email" value={form.newsletter_from_email} onChange={set("newsletter_from_email")} placeholder="news@vashdomein.bg" />
          </div>
          <div>
            <label className={labelClass}>Имейл за известия при нови коментари</label>
            <input type="email" className={inputClass} data-testid="settings-notify-email" value={form.comment_notify_email} onChange={set("comment_notify_email")} placeholder="comments@vashdomein.bg" />
          </div>
          <p className="text-xs text-slate-400">
            Известия за модерация се пращат, когато коментарите изискват одобрение. За реално
            изпращане подателят трябва да е верифициран в SendGrid.
          </p>
        </div>

        <div className="space-y-3 rounded-lg bg-slate-50 p-4">
          <div className="text-xs font-bold uppercase tracking-wide text-slate-500">Обяви</div>
          <div>
            <label className={labelClass}>Валидност на обява (дни)</label>
            <input
              type="number"
              min="1"
              className={inputClass + " max-w-[140px]"}
              data-testid="settings-listing-valid-days"
              value={form.listing_valid_days ?? 30}
              onChange={(e) => setForm((f) => ({ ...f, listing_valid_days: Number(e.target.value) || 1 }))}
            />
            <p className="mt-1 text-xs text-slate-500">
              След този брой дни от публикуването обявата се архивира автоматично и спира да се показва.
            </p>
          </div>
        </div>

        <div className="space-y-4 rounded-lg bg-slate-50 p-4">
          <div className="text-xs font-bold uppercase tracking-wide text-slate-500">Правни страници и дисклеймър</div>
          <div>
            <label className={labelClass}>Дисклеймър (показва се във футъра)</label>
            <textarea rows={3} className={inputClass} data-testid="settings-disclaimer" value={form.disclaimer_text || ""} onChange={set("disclaimer_text")} placeholder="Медията не носи отговорност за съдържанието, публикувано от потребителите…" />
          </div>
          <div>
            <label className={labelClass}>Общи условия</label>
            <textarea rows={8} className={inputClass} data-testid="settings-terms" value={form.terms_content || ""} onChange={set("terms_content")} placeholder="Текст на Общите условия. Разделяйте абзаците с нов ред." />
          </div>
          <div>
            <label className={labelClass}>Политика за поверителност</label>
            <textarea rows={8} className={inputClass} data-testid="settings-privacy" value={form.privacy_content || ""} onChange={set("privacy_content")} placeholder="Текст на Политиката за поверителност. Разделяйте абзаците с нов ред." />
          </div>
          <p className="text-xs text-slate-400">
            Страниците са достъпни от футъра: „Общи условия“ (/obshti-usloviya) и „Политика за поверителност“ (/poveritelnost).
          </p>
        </div>

        <div className="flex justify-end border-t border-slate-100 pt-4">
          <button type="submit" disabled={saving} data-testid="settings-save-btn" className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-bold text-white hover:bg-[#0041A8] disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Запази промените
          </button>
        </div>
      </form>
    </div>
  );
}
